/**
 * TimelinePage - Family photo timeline with infinite scroll
 *
 * Displays photos in a responsive grid, grouped by month (default).
 * Supports year/month/day granularity toggle and cursor-based pagination
 * with IntersectionObserver for true infinite scroll.
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  useFamily,
  useChildren,
  useTimeline,
  groupMedia,
  getFeaturedIds,
  formatGroupHeader,
  type Granularity,
} from '@wovendays/api-client';
import { useTranslation } from '@wovendays/i18n';
import { ReactionBar } from '@/components/ReactionBar';
import { ActivitySidebar } from '@/components/ActivitySidebar';
import { UploadFAB, UploadButton } from '@/components/UploadFAB';
import { Tile } from '@/components/ui/tile';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, ArrowLeft, ChevronDown, Lock } from 'lucide-react';

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const GROUP_PREVIEW_LIMIT = 30;

interface DatePillProps {
  currentGroupKey: string | null;
  groupKeys: string[];
  granularity: Granularity;
  pickerOpen: boolean;
  onTogglePicker: () => void;
  onGranularityChange: (g: Granularity) => void;
  onJumpToGroup: (key: string) => void;
  floating?: boolean;
}

function DatePill({ currentGroupKey, groupKeys, granularity, pickerOpen, onTogglePicker, onGranularityChange, onJumpToGroup, floating }: DatePillProps) {
    const { t } = useTranslation('timeline');
    const label = currentGroupKey
      ? formatGroupHeader(currentGroupKey, granularity)
      : formatGroupHeader(groupKeys[0] ?? '', granularity);

    return (
      <div className="relative" data-date-pill style={floating ? { animation: 'pillFadeIn 200ms ease-out' } : undefined}>
        <button
          onClick={onTogglePicker}
          className={cn(
            'rounded-full border px-3 py-1 font-mono text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground flex items-center gap-1.5 whitespace-nowrap',
            floating
              ? 'bg-background/90 backdrop-blur-sm shadow-md'
              : 'bg-muted/60 hover:bg-muted'
          )}
          aria-label={t('dateNav')}
        >
          {label}
          <ChevronDown className={`h-3 w-3 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
        </button>

        {pickerOpen && (
          <div className={cn(
            'absolute top-full mt-2 w-56 max-h-64 overflow-y-auto rounded-xl bg-background/95 backdrop-blur-sm border shadow-lg p-2 z-50',
            floating ? 'left-1/2 -translate-x-1/2' : 'right-0'
          )}>
            <div className="mb-2 flex gap-1 p-1" role="group" aria-label={t('groupBy')}>
              {(['year', 'month', 'day'] as const).map((g) => (
                <button
                  key={g}
                  onClick={() => onGranularityChange(g)}
                  className={`flex-1 rounded-full px-2 py-1 text-xs font-medium transition-all duration-200 ${
                    granularity === g
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'bg-muted text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {t(`common:granularity.${g}`)}
                </button>
              ))}
            </div>
            <div className="border-t my-1" />
            <div className="space-y-0.5">
              {groupKeys.map((key) => (
                <button
                  key={key}
                  onClick={() => onJumpToGroup(key)}
                  className={`w-full text-left rounded-lg px-3 py-1.5 text-xs font-mono transition-colors ${
                    key === currentGroupKey
                      ? 'bg-accent text-foreground font-semibold'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {formatGroupHeader(key, granularity)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
}

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

export function TimelinePage() {
  const { t } = useTranslation('timeline');
  const { id: familyId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialChildId = searchParams.get('childId');
  const mediaIdsFilter = searchParams.get('mediaIds');
  const [childFilter, setChildFilter] = useState<string | null>(initialChildId);
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [currentGroupKey, setCurrentGroupKey] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [headerStuck, setHeaderStuck] = useState(false);
  const groupHeaderRefs = useRef<Map<string, HTMLHeadingElement>>(new Map());
  const headerTileRef = useRef<HTMLDivElement>(null);
  const headerSentinelRef = useRef<HTMLDivElement>(null);

  const { data: familyData } = useFamily(familyId);

  const { data: childrenData } = useChildren(familyId);

  const childList = childrenData?.children ?? [];

  const handleChildFilter = (childId: string | null) => {
    setChildFilter(childId);
    if (childId) {
      setSearchParams({ childId });
    } else {
      setSearchParams({});
    }
  };

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTimeline(familyId, childFilter, mediaIdsFilter);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: '400px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Track which group header is at the top of the viewport
  useEffect(() => {
    const handleScroll = () => {
      const headers = groupHeaderRefs.current;
      let lastKey: string | null = null;
      for (const [key, el] of headers) {
        if (el.getBoundingClientRect().top <= 80) lastKey = key;
      }
      if (lastKey) setCurrentGroupKey(lastKey);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect when header becomes stuck (sentinel scrolls out of view)
  useEffect(() => {
    const el = headerSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeaderStuck(!entry?.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Close picker on click outside
  useEffect(() => {
    if (!pickerOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-date-pill]')) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pickerOpen]);

  const setGroupHeaderRef = useCallback((key: string, el: HTMLHeadingElement | null) => {
    if (el) {
      groupHeaderRefs.current.set(key, el);
    } else {
      groupHeaderRefs.current.delete(key);
    }
  }, []);

  const jumpToGroup = useCallback((groupKey: string) => {
    const el = groupHeaderRefs.current.get(groupKey);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setPickerOpen(false);
  }, []);

  const handleGranularityChange = useCallback((g: Granularity) => {
    setGranularity(g);
    setExpandedGroups(new Set());
    setPickerOpen(false);
  }, []);

  const allMedia = useMemo(() => data?.pages.flatMap((page) => page.media) ?? [], [data]);
  const mediaGroups = useMemo(() => groupMedia(allMedia, granularity), [allMedia, granularity]);

  const groupKeys = useMemo(() => Array.from(mediaGroups.keys()), [mediaGroups]);

  // Memoize arrays passed to Link state so they're not recreated per-item
  const mediaIds = useMemo(() => allMedia.map(m => m.id), [allMedia]);

  // Compute upload permission from family membership data
  const userCanUpload = (() => {
    const membership = familyData?.membership;
    if (!membership) return false;
    const { role, canUpload } = membership;
    if (role === 'owner' || role === 'admin') return true;
    if (canUpload === 'true') return true;
    if (canUpload === 'false') return false;
    const minRole = familyData?.family.minUploadRole || 'contributor';
    const hierarchy: Record<string, number> = { owner: 4, admin: 3, contributor: 2, viewer: 1 };
    return (hierarchy[role] ?? 0) >= (hierarchy[minRole] ?? 0);
  })();

  if (isLoading) {
    return (
      <div className="py-12" role="status" aria-label={t('loading')}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-foreground">{t('error.loadFailed')}</p>
        <Button onClick={() => refetch()} className="mt-3" size="sm">
          {t('error.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      <div className="flex-1 min-w-0">
      {/* Sentinel to detect when header becomes stuck */}
      <div ref={headerSentinelRef} className="h-0" />

      {/* Topbar tile */}
      <Tile ref={headerTileRef} variant="white" className={cn(
        'sticky top-0 z-40 p-5 mb-2 !overflow-visible transition-all duration-200',
        headerStuck
          ? 'bg-background/80 backdrop-blur-md shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08)] rounded-t-none'
          : ''
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="inline-flex items-center gap-1.5 shrink-0 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-wide">{t('families')}</span>
            </Link>
            <span className="text-muted-foreground/30">|</span>
            <h2 className="font-serif text-xl text-foreground truncate">
              {familyData?.family.name || t('title')}
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Child filter dropdown — fades in when stuck */}
            {childList.length > 0 && (
              <div className={cn(
                'relative transition-all duration-200',
                headerStuck ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none w-0 overflow-hidden'
              )}>
                <select
                  value={childFilter ?? ''}
                  onChange={(e) => handleChildFilter(e.target.value || null)}
                  className="h-7 appearance-none rounded-full border bg-muted/60 hover:bg-muted pl-3 pr-7 font-mono text-xs uppercase tracking-wide text-muted-foreground focus:outline-none"
                  style={{ width: '11em' }}
                  aria-label={t('filterByChild')}
                  tabIndex={headerStuck ? 0 : -1}
                >
                  <option value="">{t('allChildren')}</option>
                  {childList.map((child) => (
                    <option key={child.id} value={child.id}>{child.name}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              </div>
            )}
            {/* Date pill — inline in header */}
            {allMedia.length > 0 && (
              <DatePill
                currentGroupKey={currentGroupKey}
                groupKeys={groupKeys}
                granularity={granularity}
                pickerOpen={pickerOpen}
                onTogglePicker={() => setPickerOpen((v) => !v)}
                onGranularityChange={handleGranularityChange}
                onJumpToGroup={jumpToGroup}
              />
            )}
            <Link to={`/families/${familyId}`} className="md:hidden inline-flex items-center justify-center h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" aria-label={t('familySettings')}>
              <Settings className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Tile>

      {/* Child filter chips — collapses when header is stuck */}
      {childList.length > 0 && (
        <div
          className="grid transition-all duration-200"
          style={{ gridTemplateRows: headerStuck ? '0fr' : '1fr', marginBottom: headerStuck ? 0 : '1rem' }}
        >
        <div className="overflow-hidden">
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('filterByChild')}>
          <button
            onClick={() => handleChildFilter(null)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
              !childFilter
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
          >
            {t('all')}
          </button>
          {childList.map((child) => (
            <button
              key={child.id}
              onClick={() => handleChildFilter(child.id)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-200 ${
                childFilter === child.id
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {child.name}
            </button>
          ))}
        </div>
        </div>
        </div>
      )}

      {/* Activity filter banner */}
      {mediaIdsFilter && (
        <Tile variant="cream" className="px-4 py-3 mb-3 flex items-center justify-between">
          <p className="text-sm text-foreground">
            {t('showingFromActivity', { count: allMedia.length })}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchParams({})}
          >
            {t('clearFilter')}
          </Button>
        </Tile>
      )}

      {allMedia.length === 0 ? (
        <Tile variant="cream" className="py-16 text-center">
          <div className="mb-4 text-4xl text-muted-foreground/40">📷</div>
          <h3 className="font-serif text-lg text-foreground">{t('empty.title')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('empty.description')}
          </p>
        </Tile>
      ) : (
        <div key={childFilter ?? '__all'} className="space-y-5" style={{ animation: 'gridSwap 250ms ease-out' }}>
          {Array.from(mediaGroups.entries()).map(([groupKey, items]) => {
            const isExpanded = expandedGroups.has(groupKey);
            const displayItems = isExpanded ? items : items.slice(0, GROUP_PREVIEW_LIMIT);
            const hasMore = items.length > GROUP_PREVIEW_LIMIT && !isExpanded;
            const featuredIds = getFeaturedIds(displayItems);

            return (
              <div key={groupKey}>
                <h3
                  ref={(el) => setGroupHeaderRef(groupKey, el)}
                  data-group-key={groupKey}
                  className="mb-2 font-mono text-xs uppercase tracking-widest text-muted-foreground"
                >
                  {formatGroupHeader(groupKey, granularity)}
                </h3>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:grid-flow-dense">
                  {displayItems.map((item) => {
                    const isFeatured = featuredIds.has(item.id);
                    return (
                      <Link
                        key={item.id}
                        to={`/photo/${item.id}`}
                        state={{ familyId, childFilter, mediaIds }}
                        className={`group relative overflow-hidden rounded-2xl bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                          isFeatured
                            ? 'sm:col-span-2 sm:row-span-2 aspect-square'
                            : 'aspect-square'
                        }`}
                        aria-label={item.caption || item.uploaderName}
                      >
                        <img
                          src={item.gridUrl ?? ''}
                          alt={item.caption || ''}
                          className="h-full w-full object-cover transition-transform group-hover:scale-105"
                          loading={isFeatured ? 'eager' : 'lazy'}
                          decoding="async"
                          style={item.rotation ? { transform: `rotate(${item.rotation}deg) scale(1.2)` } : undefined}
                        />
                        {item.type === 'video' && (
                          <div className="absolute top-1.5 right-1.5 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5">
                            <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                            {item.durationMs && (
                              <span className="font-mono text-[10px] text-white">{formatDuration(item.durationMs)}</span>
                            )}
                          </div>
                        )}
                        {item.accessGroupId && (
                          <div className="absolute top-1.5 left-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/50" aria-label={t('photo:restricted')}>
                            <Lock className="h-3 w-3 text-white" data-testid="lock-icon" />
                          </div>
                        )}
                        {isFeatured && item.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/30 backdrop-blur-sm px-3 py-2">
                            <p className="font-serif text-sm italic text-white truncate">{item.caption}</p>
                          </div>
                        )}
                        <div className="absolute bottom-1 right-1 flex items-center gap-1">
                          {(item.loveCount ?? 0) > 0 && (
                            <span className="flex items-center gap-0.5 rounded-full bg-black/50 px-1.5 py-0.5 font-mono text-xs text-white" aria-label={`${item.loveCount} loves`}>
                              <span aria-hidden="true">❤️</span> {item.loveCount}
                            </span>
                          )}
                          {item.reactions && item.reactions.length > 0 && (
                            <ReactionBar reactions={item.reactions} compact />
                          )}
                          {item.commentCount > 0 && (
                            <span className="flex items-center gap-0.5 rounded-full bg-black/50 px-1.5 py-0.5 font-mono text-xs text-white" aria-label={`${item.commentCount} comments`}>
                              <span aria-hidden="true">💬</span> {item.commentCount}
                            </span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                  {hasMore && (
                    <button
                      onClick={() => setExpandedGroups(prev => new Set(prev).add(groupKey))}
                      className="aspect-square rounded-2xl bg-muted flex flex-col items-center justify-center text-muted-foreground hover:bg-accent transition-colors"
                    >
                      <span className="text-2xl font-serif">{items.length - GROUP_PREVIEW_LIMIT}+</span>
                      <span className="text-xs font-mono mt-1">{t('seeAll')}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-1" />
          {isFetchingNextPage && (
            <div className="flex justify-center py-4" role="status" aria-label={t('loadingMore')}>
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            </div>
          )}
        </div>
      )}

      {familyId && userCanUpload && <UploadFAB familyId={familyId} />}
      </div>
      {familyId && (
        <div className="hidden lg:flex lg:w-80 lg:shrink-0 lg:flex-col lg:gap-4">
          <div className="sticky top-2 space-y-2">
            {userCanUpload && <UploadButton familyId={familyId} />}
            <Link to={`/families/${familyId}`} className="block">
              <div className="px-4 py-3 flex items-center justify-center gap-2 rounded-2xl border border-border text-muted-foreground hover:bg-accent transition-colors">
                <Settings className="h-4 w-4" />
                <span className="text-sm font-medium">{t('familySettings')}</span>
              </div>
            </Link>
            <ActivitySidebar familyId={familyId} />
          </div>
        </div>
      )}
    </div>
  );
}
